<template>
  <BaseLayout
    :collapsed="effectiveCollapsed"
    @collapse-change="(v: boolean) => (sidebarCollapsed = v)"
  >
    <template #sidebar>
      <AmazonSidebar v-if="sidebarVariant === 'amazon'" :collapsed="false" />
      <BaseSidebar v-else :collapsed="effectiveCollapsed" />
    </template>

    <RouterView :key="props.routerViewKey ?? 0" />
  </BaseLayout>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import BaseLayout from '@/components/layout/BaseLayout.vue'
import BaseSidebar from '@/components/layout/BaseSidebar.vue'
import AmazonSidebar from '@/components/layout/AmazonSidebar.vue'

const SIDEBAR_COLLAPSED_KEY = 'layout.sidebarCollapsed'
const DESKTOP_BREAKPOINT = 1024

const props = withDefaults(
  defineProps<{
    routerViewKey?: number
  }>(),
  { routerViewKey: 0 }
)

const route = useRoute()

const sidebarVariant = computed(() => {
  const meta = route.meta as { sidebarVariant?: string }
  return meta?.sidebarVariant === 'amazon' ? 'amazon' : 'default'
})

// Desktop-only sidebar collapse state (persisted in localStorage)
// Initialize from localStorage synchronously so first paint matches saved state (no transition on refresh)
function getInitialSidebarCollapsed(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}
const sidebarCollapsed = ref(getInitialSidebarCollapsed())

// Set synchronously so first paint uses correct sidebar width (avoids expand→collapse animation on reload)
function getInitialIsDesktop(): boolean {
  if (typeof window === 'undefined') return false
  return window.innerWidth >= DESKTOP_BREAKPOINT
}
const isDesktop = ref(getInitialIsDesktop())

// On mobile, sidebar is always expanded (regular); collapse only applies on desktop
const effectiveCollapsed = computed(() => (isDesktop.value ? sidebarCollapsed.value : false))

// Collapsed sidebar: full-bleed content (no extra horizontal padding, flush table card) on selected routes
const usesCollapsedFullBleedLayout = computed(
  () =>
    route.name === 'products' ||
    route.path === '/product-dev/pim' ||
    route.name === 'DemandTimephase' ||
    route.path === '/reporting-dashboards/demand-timephase',
)
const layoutCollapsedStyle = computed(() => effectiveCollapsed.value && usesCollapsedFullBleedLayout.value)
provide('layoutCollapsedStyle', layoutCollapsedStyle)
// Disable main-content padding transition on these routes to avoid ResizeObserver thrash during sidebar toggle
provide('layoutDisablePaddingTransition', usesCollapsedFullBleedLayout)

function checkDesktop() {
  isDesktop.value = typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT
}

function loadSidebarCollapsed() {
  if (!isDesktop.value) return
  try {
    const raw = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
    sidebarCollapsed.value = raw === '1'
  } catch {
    // ignore
  }
}

function toggleSidebarCollapse() {
  sidebarCollapsed.value = !sidebarCollapsed.value
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed.value ? '1' : '0')
  } catch {
    // ignore
  }
}

// Show menu toggle in top bar on both mobile (hamburger) and desktop (collapse)
provide('sidebarCollapse', {
  isCollapsed: sidebarCollapsed,
  toggle: toggleSidebarCollapse,
  showToggle: { value: true }
})

watch(isDesktop, (desktop) => {
  if (desktop) loadSidebarCollapsed()
})

// Persist sidebar collapsed state whenever it changes (mirrors menu folder state pattern).
// Ensures toggling via top bar (layoutSidebarToggle → collapse-change) is also persisted.
watch(
  () => (isDesktop.value ? sidebarCollapsed.value : null),
  (collapsed) => {
    if (collapsed === null) return
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      // ignore
    }
  }
)

onMounted(() => {
  checkDesktop()
  loadSidebarCollapsed()
  window.addEventListener('resize', checkDesktop)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkDesktop)
})

// Optional: scroll main content area to top on route change
watch(
  () => route.path,
  () => {
    nextTick(() => {
      const scrollEl = document.querySelector('.main-content-area .base-scrollbar-scroll-el')
      if (scrollEl) {
        ;(scrollEl as HTMLElement).scrollTop = 0
      }
    })
  }
)
</script>
